/**
 * @fileoverview EandOPolicySupplier - Policy Type Selection Component
 *
 * UI component for E&O policy type selection (Personal vs Company-supplied).
 * Displays two buttons allowing agent to choose which type of E&O insurance they'll use.
 *
 * Business Logic:
 * - Affiliate/Associate agents: Only "Personal" option shown
 * - Signature agents+: Both "Personal" and "USABG" (company) options shown
 * - If only 1 option available: Auto-selects and hides UI
 * - Selection persists to user_metas table (key: 'profile---eo-policy-type')
 *
 * User Experience:
 * - Shows "Required" indicator when no selection made
 * - Selected button highlighted with checkmark icon
 * - Clicking same button again has no effect (already selected)
 * - Selection triggers form display in parent AgentEandOManager
 *
 * State Management:
 * - Reads policyType from EopolicyStore (MobX observable)
 * - Calls setPolicyType() to update selection
 * - Re-renders automatically on store updates (MobX observer)
 *
 * Related Documentation:
 * - /docs/modules/AgentEandOManager/components.md - Component details
 * - /docs/modules/AgentEandOManager/business-logic.md - Policy type rules
 *
 * @module components/agents/AgentEandOManager/EandOPolicySupplier
 * @requires react
 * @requires mobx-react-lite
 */

import React from 'react'
import { observer } from 'mobx-react-lite'
import { MDBContainer, MDBRow, MDBCol, MDBIcon } from 'mdb-react-ui-kit'
import EopolicyStore from './Eopolicy.store'

import './EandOPolicySupplier.component.scss'

/**
 * Policy Type Selector Component
 *
 * Renders "Personal" and/or "USABG" (company) buttons based on user permissions.
 * Manages policy type selection state via EopolicyStore.
 *
 * Display Logic:
 * - 0 permitted types → Render nothing (empty fragment)
 * - 1 permitted type → Auto-select, render nothing (hide UI)
 * - 2 permitted types → Show both buttons
 *
 * Button States:
 * - Inactive: Grey background, no icon
 * - Active: Blue background, white checkmark icon
 * - Permitted: Button visible and clickable
 * - Not Permitted: Button not rendered
 *
 * CSS Classes:
 * - `active-personal`: Personal policy selected
 * - `active-company`: Company policy selected
 * - `inactive-type`: No selection made yet
 *
 * @component
 * @param {object} props - Component props
 * @param {string[]} props.policyTypes - Allowed types: ['personal'] or ['personal', 'company']
 *
 * @returns {JSX.Element} Policy selector UI or empty fragment
 *
 * @example
 * // Affiliate agent (personal only)
 * <EandOPolicySupplier policyTypes={['personal']} />
 * // Auto-selects 'personal', renders nothing (UI hidden)
 *
 * @example
 * // Signature agent (both options)
 * <EandOPolicySupplier policyTypes={['personal', 'company']} />
 * // Renders both buttons, waits for user selection
 *
 * @example
 * // After selection
 * <EandOPolicySupplier policyTypes={['personal', 'company']} />
 * // "Personal" button shows checkmark, blue background
 * // "USABG" button greyed out, clickable
 */
const EandOPolicySupplier = ({ policyTypes }) => {
  policyTypes = policyTypes || []

  const { policyType, setPolicyType } = EopolicyStore,
    classes = [policyType ? `active-${policyType}` : '', policyType ? '' : 'inactive-type'].filter(n => n).join(' ')

  if (!policyTypes || !policyTypes.length) return <></>

  if (policyTypes.length === 1) {
    setPolicyType([...policyTypes].shift())
    return <></>
  }

  const permitted = {
    personal: policyTypes.includes('personal'),
    company: policyTypes.includes('company'),
  }

  return (
    <MDBContainer fluid id='EandOPolicySupplierComponent'>
      <MDBRow>
        <MDBCol size='12' md='6'>
          <div className='text'>
            Indicate if the E&O Policy will be personally-supplied or if you would like it supplied by USABG:
            {policyType ? (
              ''
            ) : (
              <>
                &nbsp;<span>Required</span>
              </>
            )}
          </div>
        </MDBCol>
        <MDBCol size='12' md='6'>
          <div className={`eopolicy-type-btns ${classes}`}>
            {permitted?.personal ? (
              <button
                className={policyType === 'personal' ? 'active' : ''}
                onClick={() => {
                  setPolicyType('personal')
                }}
              >
                Personal
                {policyType === 'personal' ? (
                  <>
                    &nbsp;
                    <MDBIcon fas icon='check-circle' />
                  </>
                ) : (
                  <></>
                )}
              </button>
            ) : (
              <></>
            )}
            {permitted?.company ? (
              <button
                className={policyType === 'company' ? 'active' : ''}
                onClick={() => {
                  setPolicyType('company')
                }}
              >
                USABG
                {policyType === 'company' ? (
                  <>
                    &nbsp;
                    <MDBIcon fas icon='check-circle' />
                  </>
                ) : (
                  <></>
                )}
              </button>
            ) : (
              <></>
            )}
          </div>
        </MDBCol>
      </MDBRow>
    </MDBContainer>
  )
}

export default observer(EandOPolicySupplier)
